/**
 * Contratos compartidos entre `apps/api` y `apps/web`.
 * Ninguna constante de dominio debe duplicarse: si el backend la valida,
 * el frontend la importa desde aqui.
 */

/* ------------------------------------------------------------------ roles */

export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  USER: 'USER',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: Role[] = [ROLES.SUPER_ADMIN, ROLES.USER];

export const ROLE_LABELS: Record<Role, string> = {
  SUPER_ADMIN: 'Super administrador',
  USER: 'Usuario',
};

/**
 * Solo hay dos roles, y a proposito.
 *
 * Antes habia cuatro —super administrador, administrador del ecosistema,
 * agente de soporte y usuario— y el rol decidia tambien QUE aplicaciones veia
 * cada uno. Eso obligaba a inventar un rol por cada combinacion de
 * aplicaciones, o a llenar de excepciones los que ya habia.
 *
 * Ahora el rol solo decide una cosa: si esa persona administra el ecosistema o
 * no. Las aplicaciones que ve se conceden UNA A UNA a cada persona, desde la
 * pantalla de Personas. Ver `application_access_users`.
 *
 * `SUPPORT_ROLES` y `ADMIN_ROLES` siguen existiendo porque hay rutas que las
 * piden, pero hoy las dos contienen lo mismo: quien administra atiende tambien
 * el soporte. Si algun dia vuelve a hacer falta separarlas, se separan aqui y
 * el resto del codigo no se entera.
 */
export const SUPPORT_ROLES: Role[] = [ROLES.SUPER_ADMIN];

export const ADMIN_ROLES: Role[] = [ROLES.SUPER_ADMIN];

/* ----------------------------------------------------------- aplicaciones */

export const APPLICATION_STATUS = {
  ACTIVE: 'ACTIVE',
  MAINTENANCE: 'MAINTENANCE',
  INACTIVE: 'INACTIVE',
  COMING_SOON: 'COMING_SOON',
} as const;

export type ApplicationStatus = (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS];

export const APPLICATION_STATUS_VALUES = Object.values(APPLICATION_STATUS) as ApplicationStatus[];

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  ACTIVE: 'Disponible',
  MAINTENANCE: 'Mantenimiento',
  INACTIVE: 'Inactiva',
  COMING_SOON: 'Proximamente',
};

export const AUTH_MODE = {
  OIDC: 'OIDC',
  EXTERNAL: 'EXTERNAL',
  NONE: 'NONE',
} as const;

export type AuthMode = (typeof AUTH_MODE)[keyof typeof AUTH_MODE];
export const AUTH_MODE_VALUES = Object.values(AUTH_MODE) as AuthMode[];

export const AUTH_MODE_LABELS: Record<AuthMode, string> = {
  OIDC: 'OIDC (Keycloak)',
  EXTERNAL: 'Externa',
  NONE: 'Sin autenticacion',
};

export interface Application {
  id: string;
  name: string;
  slug: string;
  shortDescription: string | null;
  description: string | null;
  iconUrl: string | null;
  launchUrl: string | null;
  category: string | null;
  status: ApplicationStatus;
  authMode: AuthMode;
  oidcClientId: string | null;
  healthUrl: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationAccessUser {
  id: string;
  applicationId: string;
  keycloakUserSub: string;
  email: string | null;
  displayName: string | null;
  createdAt: string;
}

/**
 * Una aplicacion con la lista de personas que la tienen concedida.
 *
 * No hay concesion por rol: el acceso es siempre persona a persona. Ver
 * `ROLES` para el porque.
 */
export interface ApplicationWithAccess extends Application {
  accessUsers: ApplicationAccessUser[];
}

/* --------------------------------------------------------------- tickets */

export const TICKET_STATUS = {
  OPEN: 'OPEN',
  IN_REVIEW: 'IN_REVIEW',
  IN_PROGRESS: 'IN_PROGRESS',
  WAITING_USER: 'WAITING_USER',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
  REOPENED: 'REOPENED',
} as const;

export type TicketStatus = (typeof TICKET_STATUS)[keyof typeof TICKET_STATUS];
export const TICKET_STATUS_VALUES = Object.values(TICKET_STATUS) as TicketStatus[];

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  OPEN: 'Abierto',
  IN_REVIEW: 'En revision',
  IN_PROGRESS: 'En progreso',
  WAITING_USER: 'Esperando usuario',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
  REOPENED: 'Reabierto',
};

/** Estados que cuentan como "ticket todavia vivo". */
export const OPEN_TICKET_STATUSES: TicketStatus[] = [
  TICKET_STATUS.OPEN,
  TICKET_STATUS.IN_REVIEW,
  TICKET_STATUS.IN_PROGRESS,
  TICKET_STATUS.WAITING_USER,
  TICKET_STATUS.REOPENED,
];

export const TICKET_PRIORITY = {
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const;

export type TicketPriority = (typeof TICKET_PRIORITY)[keyof typeof TICKET_PRIORITY];
export const TICKET_PRIORITY_VALUES = Object.values(TICKET_PRIORITY) as TicketPriority[];

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  LOW: 'Baja',
  NORMAL: 'Normal',
  HIGH: 'Alta',
  CRITICAL: 'Critica',
};

export const TICKET_CATEGORY = {
  BUG: 'BUG',
  LOGIN: 'LOGIN',
  ACCESS: 'ACCESS',
  PERFORMANCE: 'PERFORMANCE',
  INCORRECT_DATA: 'INCORRECT_DATA',
  FEATURE_NOT_WORKING: 'FEATURE_NOT_WORKING',
  HELP_REQUEST: 'HELP_REQUEST',
  OTHER: 'OTHER',
} as const;

export type TicketCategory = (typeof TICKET_CATEGORY)[keyof typeof TICKET_CATEGORY];
export const TICKET_CATEGORY_VALUES = Object.values(TICKET_CATEGORY) as TicketCategory[];

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  BUG: 'Error / Bug',
  LOGIN: 'Inicio de sesion',
  ACCESS: 'Acceso / permisos',
  PERFORMANCE: 'Rendimiento',
  INCORRECT_DATA: 'Informacion incorrecta',
  FEATURE_NOT_WORKING: 'Funcion que no funciona',
  HELP_REQUEST: 'Solicitud de ayuda',
  OTHER: 'Otro',
};

/**
 * Un ticket puede apuntar a una aplicacion registrada, al propio Ecosystem
 * o a algo general. Asi el modulo de soporte funciona con cero aplicaciones.
 */
export const TICKET_SCOPE = {
  APPLICATION: 'APPLICATION',
  ECOSYSTEM: 'ECOSYSTEM',
  OTHER: 'OTHER',
} as const;

export type TicketScope = (typeof TICKET_SCOPE)[keyof typeof TICKET_SCOPE];
export const TICKET_SCOPE_VALUES = Object.values(TICKET_SCOPE) as TicketScope[];

export const ECOSYSTEM_SCOPE_LABEL = 'ED Ecosystem';
export const OTHER_SCOPE_LABEL = 'Otro / General';

export const COMMENT_VISIBILITY = {
  PUBLIC: 'PUBLIC',
  INTERNAL: 'INTERNAL',
} as const;

export type CommentVisibility = (typeof COMMENT_VISIBILITY)[keyof typeof COMMENT_VISIBILITY];
export const COMMENT_VISIBILITY_VALUES = Object.values(COMMENT_VISIBILITY) as CommentVisibility[];

export interface TicketTechnicalContext {
  browser: string | null;
  userAgent: string | null;
  operatingSystem: string | null;
  originUrl: string | null;
}

export interface TicketAttachment {
  id: string;
  ticketId: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBySub: string;
  createdAt: string;
  downloadUrl: string;
}

export interface TicketSummary {
  id: string;
  ticketNumber: string;
  subject: string;
  category: TicketCategory;
  status: TicketStatus;
  priority: TicketPriority;
  reportedPriority: TicketPriority;
  scope: TicketScope;
  applicationId: string | null;
  applicationName: string | null;
  createdBySub: string;
  createdByEmail: string | null;
  createdByName: string | null;
  assignedToSub: string | null;
  assignedToName: string | null;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
}

export interface Ticket extends TicketSummary {
  description: string;
  actionPerformed: string | null;
  stepsToReproduce: string | null;
  errorMessage: string | null;
  expectedResult: string | null;
  actualResult: string | null;
  assignedToEmail: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  technical: TicketTechnicalContext;
  attachments: TicketAttachment[];
}

export interface TicketComment {
  id: string;
  ticketId: string;
  visibility: CommentVisibility;
  body: string;
  authorSub: string;
  authorName: string | null;
  authorEmail: string | null;
  authorIsStaff: boolean;
  createdAt: string;
}

export const TICKET_EVENT_TYPE = {
  CREATED: 'CREATED',
  ASSIGNED: 'ASSIGNED',
  UNASSIGNED: 'UNASSIGNED',
  STATUS_CHANGED: 'STATUS_CHANGED',
  PRIORITY_CHANGED: 'PRIORITY_CHANGED',
  COMMENT_PUBLIC: 'COMMENT_PUBLIC',
  COMMENT_INTERNAL: 'COMMENT_INTERNAL',
  ATTACHMENT_ADDED: 'ATTACHMENT_ADDED',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
  /**
   * Cierre por inactividad, sin que nadie pulse nada.
   *
   * Es un tipo aparte y no un `CLOSED` con el autor vacio: quien lea la
   * historia del ticket meses despues tiene que poder distinguir «lo cerro
   * alguien» de «se cerro solo porque nadie volvio».
   */
  AUTO_CLOSED: 'AUTO_CLOSED',
  REOPENED: 'REOPENED',
} as const;

export type TicketEventType = (typeof TICKET_EVENT_TYPE)[keyof typeof TICKET_EVENT_TYPE];

export const TICKET_EVENT_LABELS: Record<TicketEventType, string> = {
  CREATED: 'Ticket creado',
  ASSIGNED: 'Ticket asignado',
  UNASSIGNED: 'Asignacion retirada',
  STATUS_CHANGED: 'Estado cambiado',
  PRIORITY_CHANGED: 'Prioridad cambiada',
  COMMENT_PUBLIC: 'Nueva respuesta',
  COMMENT_INTERNAL: 'Nota interna',
  ATTACHMENT_ADDED: 'Adjunto agregado',
  RESOLVED: 'Ticket resuelto',
  CLOSED: 'Ticket cerrado',
  AUTO_CLOSED: 'Cerrado automaticamente por inactividad',
  REOPENED: 'Ticket reabierto',
};

export interface TicketEvent {
  id: string;
  ticketId: string;
  eventType: TicketEventType;
  actorSub: string | null;
  actorName: string | null;
  fromValue: string | null;
  toValue: string | null;
  message: string | null;
  createdAt: string;
}

/* ------------------------------------------------------------- auditoria */

export const AUDIT_ACTION = {
  USER_PASSWORD_CHANGED: 'USER_PASSWORD_CHANGED',
  USER_STATUS_CHANGED: 'USER_STATUS_CHANGED',
  APPLICATION_CREATED: 'APPLICATION_CREATED',
  APPLICATION_UPDATED: 'APPLICATION_UPDATED',
  APPLICATION_DISABLED: 'APPLICATION_DISABLED',
  APPLICATION_ACCESS_GRANTED: 'APPLICATION_ACCESS_GRANTED',
  APPLICATION_ACCESS_REVOKED: 'APPLICATION_ACCESS_REVOKED',
  TICKET_CREATED: 'TICKET_CREATED',
  TICKET_ASSIGNED: 'TICKET_ASSIGNED',
  TICKET_STATUS_CHANGED: 'TICKET_STATUS_CHANGED',
  TICKET_PRIORITY_CHANGED: 'TICKET_PRIORITY_CHANGED',
  TICKET_COMMENTED: 'TICKET_COMMENTED',
  TICKET_ATTACHMENT_ADDED: 'TICKET_ATTACHMENT_ADDED',
  TICKET_RESOLVED: 'TICKET_RESOLVED',
  TICKET_REOPENED: 'TICKET_REOPENED',
  TICKET_CLOSED: 'TICKET_CLOSED',
  ANNOUNCEMENT_CREATED: 'ANNOUNCEMENT_CREATED',
  ANNOUNCEMENT_UPDATED: 'ANNOUNCEMENT_UPDATED',
  ANNOUNCEMENT_DELETED: 'ANNOUNCEMENT_DELETED',
} as const;

export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];
export const AUDIT_ACTION_VALUES = Object.values(AUDIT_ACTION) as AuditAction[];

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  USER_PASSWORD_CHANGED: 'Cambio de contraseña',
  USER_STATUS_CHANGED: 'Cambio de estado de cuenta',
  ANNOUNCEMENT_CREATED: 'Anuncio creado',
  ANNOUNCEMENT_UPDATED: 'Anuncio editado',
  ANNOUNCEMENT_DELETED: 'Anuncio borrado',
  APPLICATION_CREATED: 'Aplicacion creada',
  APPLICATION_UPDATED: 'Aplicacion actualizada',
  APPLICATION_DISABLED: 'Aplicacion desactivada',
  APPLICATION_ACCESS_GRANTED: 'Acceso concedido',
  APPLICATION_ACCESS_REVOKED: 'Acceso revocado',
  TICKET_CREATED: 'Ticket creado',
  TICKET_ASSIGNED: 'Ticket asignado',
  TICKET_STATUS_CHANGED: 'Estado de ticket cambiado',
  TICKET_PRIORITY_CHANGED: 'Prioridad de ticket cambiada',
  TICKET_COMMENTED: 'Comentario en ticket',
  TICKET_ATTACHMENT_ADDED: 'Adjunto agregado',
  TICKET_RESOLVED: 'Ticket resuelto',
  TICKET_REOPENED: 'Ticket reabierto',
  TICKET_CLOSED: 'Ticket cerrado',
};

export interface AuditLogEntry {
  id: number;
  actorSub: string | null;
  actorEmail: string | null;
  action: AuditAction;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

/* ------------------------------------------------------------- identidad */

export interface CurrentUser {
  sub: string;
  username: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string;
  roles: Role[];
  isSupport: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

/* -------------------------------------------------------- notificaciones */

/**
 * Clases de anuncio.
 *
 * Cuatro y no mas: en cuanto hay diez, quien escribe el anuncio pierde un
 * minuto eligiendo y quien lo lee deja de distinguirlas.
 */
export const ANNOUNCEMENT_KIND = {
  /** Algo que conviene saber. El caso normal. */
  INFO: 'INFO',
  /** Una aplicacion cambio: hay algo nuevo o algo funciona distinto. */
  UPDATE: 'UPDATE',
  /** Habra un rato sin servicio, con su fecha. */
  MAINTENANCE: 'MAINTENANCE',
  /** Algo esta pasando AHORA y afecta al trabajo. */
  ALERT: 'ALERT',
} as const;

export type AnnouncementKind = (typeof ANNOUNCEMENT_KIND)[keyof typeof ANNOUNCEMENT_KIND];

export const ANNOUNCEMENT_KIND_LABEL: Record<AnnouncementKind, string> = {
  INFO: 'Informacion',
  UPDATE: 'Novedad',
  MAINTENANCE: 'Mantenimiento',
  ALERT: 'Aviso',
};

/**
 * Un anuncio del canal informativo.
 *
 * `publishedAt` en null significa borrador: existe, pero no lo ve nadie mas
 * que quien lo escribe.
 */
export interface Announcement {
  id: string;
  revision: number;
  firstPublishedAt: string | null;
  title: string;
  body: string;
  kind: AnnouncementKind;
  publishedAt: string | null;
  authorName: string | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * A quien va dirigida una notificacion.
 *
 * `USER` es una persona concreta; `ROLE`, quien tenga cierta capacidad;
 * `ALL`, cualquiera con sesion. Existe porque el ecosistema no guarda la lista
 * de usuarios —viven en Keycloak—, asi que un aviso para todos no se puede
 * escribir repartido en una fila por empleado.
 */
export const NOTIFICATION_AUDIENCE = {
  USER: 'USER',
  ROLE: 'ROLE',
  ALL: 'ALL',
} as const;

export type NotificationAudience =
  (typeof NOTIFICATION_AUDIENCE)[keyof typeof NOTIFICATION_AUDIENCE];

/**
 * Los circulos a los que se puede dirigir una notificacion de tipo `ROLE`.
 *
 * Son CAPACIDADES, no roles sueltos, y se contienen entre si:
 * SUPER_ADMIN esta dentro de ADMIN, y ADMIN dentro de SUPPORT. Asi un suceso
 * genera siempre UNA fila y quien manda mas ve tambien lo de quien manda
 * menos, sin recibir nada por duplicado.
 */
export const NOTIFICATION_CIRCLE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  SUPPORT: 'SUPPORT',
} as const;

export type NotificationCircle =
  (typeof NOTIFICATION_CIRCLE)[keyof typeof NOTIFICATION_CIRCLE];

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  readAt: string | null;
  createdAt: string;
  /** Para distinguir en la interfaz un aviso personal de uno para todos. */
  audience: NotificationAudience;
}

/* ------------------------------------------------------------- dashboard */

export interface DashboardTicketStats {
  open: number;
  inProgress: number;
  waitingUser: number;
  resolved: number;
  total: number;
}

export interface DashboardResponse {
  user: CurrentUser;
  applications: Application[];
  ticketStats: DashboardTicketStats;
  recentTickets: TicketSummary[];
  supportQueue: { unassigned: number; assignedToMe: number } | null;
}

/* ------------------------------------------------------------ envolturas */

export interface ApiErrorBody {
  success: false;
  error: {
    code: string;
    message: string;
    requestId: string;
    details?: unknown;
  };
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/* --------------------------------------------------------------- limites */

export const ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_PER_TICKET = 10;

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'text/plain',
] as const;

export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.pdf',
  '.txt',
  '.log',
] as const;
